import { useTextContentElementToAdd } from 'components/WorkspaceEditor/Workspace2Store'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useCKEditorToggle } from './useCKEditorToggle'

/**
 * Global focus tracker to prevent simultaneous focusing of multiple editors.
 * This singleton exists outside React's component lifecycle to provide
 * synchronous coordination between all editor instances.
 */
const FocusTracker = {
    activeEditorId: null as string | null,
    /** Callback to deactivate the currently active editor */
    activeEditorDeactivateCallback: null as (() => void) | null,

    /**
     * Registers an editor as the active one, forcefully deactivating any
     * previously active editor first to prevent multiple editors in edit mode.
     */
    registerEditor(
        editorId: string | undefined,
        callback?: () => void,
        deactivateCallback?: () => void
    ): boolean {
        if (!editorId) {
            return false
        }

        // If same editor is already active, just update callback
        if (this.activeEditorId === editorId) {
            this.activeEditorDeactivateCallback = deactivateCallback ?? null
            callback?.()
            return true
        }

        // Force deactivate any currently active editor first
        if (this.activeEditorId && this.activeEditorDeactivateCallback) {
            try {
                this.activeEditorDeactivateCallback()
            } catch (e) {
                console.error('Failed to deactivate editor:', e)
            }
        }

        // Set this editor as active
        this.activeEditorId = editorId
        this.activeEditorDeactivateCallback = deactivateCallback ?? null
        callback?.()
        return true
    },

    unregisterEditor(editorId: string | undefined): void {
        if (editorId && this.activeEditorId === editorId) {
            this.activeEditorId = null
            this.activeEditorDeactivateCallback = null
        }
    },

    /**
     * Check if a specific editor is currently the active one.
     */
    isEditorActive(editorId: string | undefined): boolean {
        return editorId != null && this.activeEditorId === editorId
    },

    /**
     * Force deactivation of any active editor.
     */
    forceDeactivateActive(): boolean {
        if (this.activeEditorDeactivateCallback) {
            try {
                this.activeEditorDeactivateCallback()
                this.activeEditorId = null
                this.activeEditorDeactivateCallback = null
                return true
            } catch (e) {
                console.error('Failed to deactivate editor:', e)
            }
        }
        return false
    },

    /**
     * Reset the FocusTracker state. Used for testing purposes.
     * @internal
     */
    _reset(): void {
        this.activeEditorId = null
        this.activeEditorDeactivateCallback = null
    },
}

// Export for testing
export const _FocusTrackerTestUtils = {
    reset: () => FocusTracker._reset(),
}

/** Props for the useContentEditorFocus hook. */
interface UseContentEditorFocusProps {
    /** The ID of the content item to be focused. */
    contentItemId?: string
    /** The optional handler to activate CKEditor. */
    activate?: () => void
    /** Optional control type to distinguish between different editor types */
    controlType?: 'plainText' | 'replacementOption'
}

/**
 * Custom hook that handles setting focus on `editingTextContentElement` by matching its id.
 * Uses a global focus tracker to prevent simultaneous focusing of multiple editors.
 *
 * @param {UseContentEditorFocusProps} props - Props for the hook.
 * @param {string} props.contentItemId - The ID of the content item to be focused.
 * @param {object} props.activate - The optional handler to activate CKEditor.
 * @param {string} props.controlType - Optional type to distinguish different editors.
 * @returns The CKEditor (or type of T) ref to focus with.
 */
export const useContentEditorFocus = <T extends { focus: () => void }>({
    contentItemId,
    activate,
    controlType = 'plainText',
}: UseContentEditorFocusProps) => {
    const editorFocusRef: MutableRefObject<T | null> = useRef(null)
    const ckeToggle = useCKEditorToggle(contentItemId)
    const textContentElementToAdd = useTextContentElementToAdd()

    // Create a unique ID combining contentItemId and controlType
    const uniqueEditorId = contentItemId
        ? `${contentItemId}:${controlType}`
        : undefined

    // Manually focus the editor with proper prioritization
    const focusEditor = useCallback(() => {
        if (!uniqueEditorId) return

        FocusTracker.registerEditor(
            uniqueEditorId,
            () => {
                const shouldProcessContentItem =
                    contentItemId &&
                    (!textContentElementToAdd ||
                        textContentElementToAdd.id === contentItemId)
                if (shouldProcessContentItem) {
                    activate?.()

                    // Allow DOM to update before focusing
                    requestAnimationFrame(() => {
                        if (
                            editorFocusRef.current &&
                            FocusTracker.isEditorActive(uniqueEditorId)
                        ) {
                            editorFocusRef.current.focus()
                        }
                    })
                }
            },
            () => ckeToggle.deactivate()
        )
    }, [
        uniqueEditorId,
        contentItemId,
        textContentElementToAdd,
        activate,
        editorFocusRef,
        ckeToggle,
    ])

    // Defocus the editor when there's an add operation
    const unFocusEditor = useCallback(() => {
        if (editorFocusRef?.current) {
            editorFocusRef.current = null
            ckeToggle.deactivate()

            // Unregister this editor from the focus tracker
            if (uniqueEditorId) {
                FocusTracker.unregisterEditor(uniqueEditorId)
            }
        }
    }, [editorFocusRef, ckeToggle, uniqueEditorId])

    // Clean up when the component is unmounted
    useEffect(() => {
        return () => {
            if (uniqueEditorId) {
                FocusTracker.unregisterEditor(uniqueEditorId)
            }
        }
    }, [uniqueEditorId])

    return { editorFocusRef, unFocusEditor, focusEditor }
}
